/**
 * ProgressiveContentArea
 *
 * Right-panel accordion that surfaces step output cards as each pipeline phase
 * completes. Each card animates in with a slide-up + fade transition.
 *
 * Only phases that have associated data AND a registered card renderer are shown.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { FileEdit, MessageSquare, PenLine, Search } from 'lucide-react'
import type { PhaseEvent } from './GenerationSteps'
import { PIPELINE_STEPS } from './GenerationSteps'
import { ResearchCard } from './step-cards/ResearchCard'
import { PlanCard } from './step-cards/PlanCard'
import { CritiqueCard, type CritiqueData } from './step-cards/CritiqueCard'
import { GenerationCard } from './step-cards/GenerationCard'

interface CardMeta {
  icon: React.ReactNode
  label: string
  headerClass: string
}

const CARD_META: Record<string, CardMeta> = {
  researching: {
    icon: <Search className="w-3.5 h-3.5" />,
    label: 'Investigacion',
    headerClass: 'text-blue-700 bg-blue-50 border-blue-100',
  },
  planning: {
    icon: <FileEdit className="w-3.5 h-3.5" />,
    label: 'Estructura',
    headerClass: 'text-indigo-700 bg-indigo-50 border-indigo-100',
  },
  critiquing: {
    icon: <MessageSquare className="w-3.5 h-3.5" />,
    label: 'Critica',
    headerClass: 'text-amber-700 bg-amber-50 border-amber-100',
  },
  generating: {
    icon: <PenLine className="w-3.5 h-3.5" />,
    label: 'Generando',
    headerClass: 'text-violet-700 bg-violet-50 border-violet-100',
  },
}

function renderStepContent(
  phaseKey: string,
  data: Record<string, unknown>,
  streamBuffer?: string,
): React.ReactNode | null {
  if (phaseKey === 'researching' && Array.isArray(data.sources)) {
    return <ResearchCard data={data} />
  }

  if (phaseKey === 'planning' && data.outline !== undefined) {
    return <PlanCard data={data} />
  }

  if (phaseKey === 'critiquing' && typeof data.score === 'number') {
    const critiqueData: CritiqueData = {
      score: data.score,
      feedback: (data.feedback as CritiqueData['feedback']) ?? null,
    }
    return <CritiqueCard data={critiqueData} />
  }

  if (phaseKey === 'generating') {
    return <GenerationCard streamBuffer={streamBuffer ?? ''} />
  }

  return null
}

interface ProgressiveContentAreaProps {
  phaseEvents: Record<string, PhaseEvent>
  activePhase?: string | null
  streamBuffer?: string
}

export function ProgressiveContentArea({
  phaseEvents,
  activePhase,
  streamBuffer = '',
}: ProgressiveContentAreaProps) {
  // Maintain pipeline order; show phases that either have completed data or are actively generating
  const visibleSteps = PIPELINE_STEPS.filter((step) => {
    if (!(step.phaseKey in CARD_META)) return false
    if (step.phaseKey === 'generating') {
      return activePhase === 'generating' || step.phaseKey in phaseEvents
    }
    const event = phaseEvents[step.phaseKey]
    return event?.data != null
  })

  return (
    <div className="flex flex-col gap-3">
      <AnimatePresence initial={false}>
        {visibleSteps.map((step) => {
          const event = phaseEvents[step.phaseKey]
          const meta = CARD_META[step.phaseKey]
          const data = (event?.data as Record<string, unknown>) ?? {}
          const content = renderStepContent(step.phaseKey, data, streamBuffer)

          if (!content) return null

          return (
            <motion.div
              key={step.phaseKey}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="rounded-xl border border-slate-100 bg-white shadow-sm overflow-hidden"
            >
              <div
                className={`flex items-center gap-2 border-b px-4 py-2.5 ${meta.headerClass}`}
              >
                {meta.icon}
                <span className="text-xs font-semibold uppercase tracking-wide">
                  {meta.label}
                </span>
              </div>
              <div className="px-4 py-3">{content}</div>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}
